import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Rocket, Target, Sparkles, Loader2, Mail, Flame, ThermometerSun, Snowflake,
  Send, Wand2, ArrowLeft, Users, TrendingUp, Layers, RefreshCw,
  Upload, FileSpreadsheet, Trash2, Building2, Briefcase,
} from "lucide-react";

const SEG_STYLE = {
  hot: { cls: "bg-[#FEE2E2] text-[#B91C1C]", icon: Flame, label: "Hot" },
  warm: { cls: "bg-[#FEF3C7] text-[#92600A]", icon: ThermometerSun, label: "Warm" },
  cold: { cls: "bg-[#E0F2FE] text-[#0369A1]", icon: Snowflake, label: "Cold" },
};

export default function GrowthHub() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [icp, setIcp] = useState(null);
  const [icpNotes, setIcpNotes] = useState("");
  const [icpLoading, setIcpLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [summary, setSummary] = useState({ total: 0, hot: 0, warm: 0, cold: 0, contacted: 0 });
  const [loadingLeads, setLoadingLeads] = useState(true);

  // draft composer
  const [draftFor, setDraftFor] = useState(null);
  const [draftType, setDraftType] = useState("lead");
  const [draft, setDraft] = useState({ subject: "", body: "", to_email: "" });
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);

  // prospects (CSV outbound)
  const [prospects, setProspects] = useState([]);
  const [prospectSummary, setProspectSummary] = useState({ total: 0, contacted: 0 });
  const [uploading, setUploading] = useState(false);
  const [loadingProspects, setLoadingProspects] = useState(true);

  // sequence
  const [seqAudience, setSeqAudience] = useState("Profitable LLC owners ($80k+ net profit) not yet on S-Corp");
  const [seqLoading, setSeqLoading] = useState(false);
  const [sequence, setSequence] = useState(null);

  const loadLeads = () => {
    setLoadingLeads(true);
    api.get("/admin/growth/leads")
      .then(({ data }) => { setLeads(data.leads); setSummary(data.summary); })
      .catch(() => toast.error("Could not load leads"))
      .finally(() => setLoadingLeads(false));
  };

  const loadProspects = () => {
    setLoadingProspects(true);
    api.get("/admin/growth/prospects")
      .then(({ data }) => { setProspects(data.prospects); setProspectSummary(data.summary); })
      .catch(() => toast.error("Could not load prospects"))
      .finally(() => setLoadingProspects(false));
  };

  useEffect(() => {
    if (user && user.role === "admin") {
      api.get("/admin/growth/icp").then(({ data }) => { if (data && data.summary) setIcp(data); }).catch(() => {});
      loadLeads();
      loadProspects();
    }
  }, [user]);

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const generateIcp = async () => {
    setIcpLoading(true);
    try {
      const { data } = await api.post("/admin/growth/icp", { notes: icpNotes || null });
      setIcp(data);
      toast.success("ICP generated");
    } catch {
      toast.error("Could not generate ICP. Try again.");
    } finally {
      setIcpLoading(false);
    }
  };

  const openDraft = async (entity, type = "lead") => {
    setDraftFor(entity);
    setDraftType(type);
    setDraft({ subject: "", body: "", to_email: entity.email || "" });
    setDrafting(true);
    try {
      const payload = type === "prospect" ? { prospect_id: entity.id } : { lead_id: entity.id };
      const { data } = await api.post("/admin/growth/draft", payload);
      setDraft({ subject: data.subject, body: data.body, to_email: data.to_email || entity.email || "" });
    } catch {
      toast.error("Could not draft email. Try again.");
      setDraftFor(null);
    } finally {
      setDrafting(false);
    }
  };

  const sendDraft = async () => {
    if (!draft.to_email || !draft.subject || !draft.body) {
      toast.error("Recipient, subject and body are required.");
      return;
    }
    setSending(true);
    try {
      await api.post("/admin/growth/send", {
        to_email: draft.to_email, subject: draft.subject, body: draft.body,
        lead_id: draftType === "lead" ? draftFor?.id || null : null,
        prospect_id: draftType === "prospect" ? draftFor?.id || null : null,
      });
      toast.success(`Email sent to ${draft.to_email}`);
      setDraftFor(null);
      if (draftType === "prospect") loadProspects(); else loadLeads();
    } catch (e) {
      toast.error("Could not send email.");
    } finally {
      setSending(false);
    }
  };

  const uploadCsv = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".csv";
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const fd = new FormData();
      fd.append("file", file);
      setUploading(true);
      try {
        const { data } = await api.post("/admin/growth/prospects/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success(`Imported ${data.imported} prospect${data.imported === 1 ? "" : "s"}${data.skipped ? ` · ${data.skipped} skipped` : ""}`);
        loadProspects();
      } catch (err) {
        toast.error("Could not import CSV. Check the file format.");
      } finally {
        setUploading(false);
      }
    };
    input.click();
  };

  const deleteProspect = async (id) => {
    try {
      await api.delete(`/admin/growth/prospects/${id}`);
      setProspects((p) => p.filter((x) => x.id !== id));
      setProspectSummary((s) => ({ ...s, total: Math.max(0, s.total - 1) }));
    } catch {
      toast.error("Could not remove prospect.");
    }
  };

  const genSequence = async () => {
    setSeqLoading(true);
    try {
      const { data } = await api.post("/admin/growth/sequence", { audience: seqAudience });
      setSequence(data.emails);
      toast.success("Sequence generated");
    } catch {
      toast.error("Could not generate sequence.");
    } finally {
      setSeqLoading(false);
    }
  };

  const statCards = [
    { label: "Total leads", value: summary.total, icon: Users },
    { label: "Hot", value: summary.hot, icon: Flame },
    { label: "Warm", value: summary.warm, icon: ThermometerSun },
    { label: "Contacted", value: summary.contacted, icon: Send },
  ];

  return (
    <div className="min-h-screen bg-[#F9F9F8] font-body">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 sm:px-12 pt-28 pb-20">
        <button onClick={() => navigate("/admin")} data-testid="back-to-admin"
          className="inline-flex items-center gap-1.5 text-sm text-[#595D5A] hover:text-[#114A31]">
          <ArrowLeft className="h-4 w-4" /> Back to Admin
        </button>
        <span className="mt-4 text-xs tracking-[0.2em] uppercase font-bold text-[#114A31] flex items-center gap-2">
          <Rocket className="h-3.5 w-3.5" /> Growth Hub
        </span>
        <h1 className="mt-3 font-heading text-3xl lg:text-4xl tracking-tight font-medium text-[#1C1C1C]">
          Find &amp; convert customers
        </h1>
        <p className="mt-2 text-[#595D5A]">
          Your AI growth agent scores every lead, drafts personalized outreach, and builds campaigns.
        </p>

        {/* Stats */}
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="growth-stats">
          {statCards.map((s) => (
            <div key={s.label} className="bg-white border border-[#E5E5E5] rounded-2xl p-5">
              <s.icon className="h-5 w-5 text-[#114A31]" />
              <p className="mt-3 font-heading text-3xl font-bold text-[#1C1C1C]">{s.value}</p>
              <p className="text-sm text-[#595D5A]">{s.label}</p>
            </div>
          ))}
        </div>

        {/* ICP Builder */}
        <section className="mt-10 bg-white border border-[#E5E5E5] rounded-2xl p-6 lg:p-8" data-testid="icp-section">
          <div className="flex items-center gap-2">
            <span className="h-8 w-8 rounded-lg bg-[#114A31] flex items-center justify-center">
              <Target className="h-4 w-4 text-white" />
            </span>
            <h2 className="font-heading text-xl font-semibold text-[#1C1C1C]">Ideal Customer Profile</h2>
          </div>
          {!icp ? (
            <div className="mt-4 max-w-2xl">
              <p className="text-sm text-[#595D5A]">
                Let the AI define exactly who to target and how to message them.
              </p>
              <Textarea
                value={icpNotes} onChange={(e) => setIcpNotes(e.target.value)} data-testid="icp-notes"
                placeholder="Optional: add any context (niche, location, business size you prefer)…"
                className="mt-3 rounded-xl border-[#E5E5E5] min-h-[80px]"
              />
              <Button onClick={generateIcp} disabled={icpLoading} data-testid="generate-icp-btn"
                className="mt-4 rounded-full bg-[#114A31] hover:bg-[#1A6B46] text-white px-6 py-5">
                {icpLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Wand2 className="h-4 w-4 mr-2" /> Generate ICP</>}
              </Button>
            </div>
          ) : (
            <div className="mt-4">
              <p className="text-[#1C1C1C]">{icp.summary}</p>
              <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
                {(icp.segments || []).map((seg, i) => (
                  <div key={i} className="rounded-xl border border-[#E5E5E5] bg-[#F9F9F8] p-4" data-testid={`icp-segment-${i}`}>
                    <p className="font-heading font-semibold text-[#114A31]">{seg.name}</p>
                    <p className="mt-1 text-xs text-[#595D5A]"><strong>Who:</strong> {seg.who}</p>
                    <p className="mt-1 text-xs text-[#595D5A]"><strong>Pain:</strong> {seg.pain}</p>
                    <p className="mt-1 text-xs text-[#114A31]"><strong>Hook:</strong> {seg.hook}</p>
                  </div>
                ))}
              </div>
              <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                {icp.titles?.length > 0 && (
                  <div><p className="text-xs font-bold uppercase tracking-wide text-[#595D5A] mb-1.5">Target roles</p>
                    <div className="flex flex-wrap gap-1.5">{icp.titles.map((t, i) => (
                      <span key={i} className="text-xs bg-[#E6F0EB] text-[#114A31] px-2.5 py-1 rounded-full">{t}</span>))}</div></div>
                )}
                {icp.channels?.length > 0 && (
                  <div><p className="text-xs font-bold uppercase tracking-wide text-[#595D5A] mb-1.5">Channels</p>
                    <ul className="space-y-1 text-[#1C1C1C]">{icp.channels.map((c, i) => <li key={i}>• {c}</li>)}</ul></div>
                )}
                {icp.messaging_angles?.length > 0 && (
                  <div><p className="text-xs font-bold uppercase tracking-wide text-[#595D5A] mb-1.5">Messaging angles</p>
                    <ul className="space-y-1 text-[#1C1C1C]">{icp.messaging_angles.map((c, i) => <li key={i}>• {c}</li>)}</ul></div>
                )}
              </div>
              <Button onClick={() => setIcp(null)} variant="outline" data-testid="regenerate-icp-btn"
                className="mt-5 rounded-full border-[#E5E5E5] hover:border-[#114A31] text-[#1C1C1C]">
                <RefreshCw className="h-4 w-4 mr-2" /> Regenerate
              </Button>
            </div>
          )}
        </section>

        {/* Lead scoring + outreach */}
        <section className="mt-8 bg-white border border-[#E5E5E5] rounded-2xl" data-testid="leads-section">
          <div className="px-6 py-5 border-b border-[#E5E5E5] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="h-8 w-8 rounded-lg bg-[#114A31] flex items-center justify-center">
                <TrendingUp className="h-4 w-4 text-white" />
              </span>
              <h2 className="font-heading text-xl font-semibold text-[#1C1C1C]">Scored leads</h2>
            </div>
            <button onClick={loadLeads} data-testid="refresh-leads" className="text-[#595D5A] hover:text-[#114A31]">
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
          <div className="p-4">
            {loadingLeads ? (
              <div className="py-10 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#114A31]" /></div>
            ) : leads.length === 0 ? (
              <div className="py-12 text-center text-[#595D5A]" data-testid="leads-empty">
                <Target className="h-9 w-9 mx-auto mb-2" />
                <p className="text-sm">No leads yet. Calculator users and chatbot visitors will appear here, scored.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {leads.map((l) => {
                  const seg = SEG_STYLE[l.segment] || SEG_STYLE.cold;
                  return (
                    <div key={l.id} data-testid={`growth-lead-${l.id}`}
                      className="rounded-xl border border-[#E5E5E5] p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                      <div className="flex items-center gap-2 sm:w-16 shrink-0">
                        <span className="font-heading text-2xl font-bold text-[#114A31]">{l.score}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Mail className="h-4 w-4 text-[#114A31] shrink-0" />
                          <span className="text-sm font-medium text-[#1C1C1C] truncate">{l.email || "no email"}</span>
                          <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full flex items-center gap-1 ${seg.cls}`}>
                            <seg.icon className="h-3 w-3" /> {seg.label}
                          </span>
                          {l.status === "contacted" && (
                            <span className="text-[10px] uppercase tracking-wide bg-[#DCFCE7] text-[#166534] px-2 py-0.5 rounded-full">Contacted</span>
                          )}
                        </div>
                        {l.context && <p className="mt-1 text-xs text-[#595D5A] line-clamp-1">{l.context}</p>}
                      </div>
                      <Button onClick={() => openDraft(l)} disabled={!l.email} data-testid={`draft-btn-${l.id}`}
                        className="rounded-full bg-[#114A31] hover:bg-[#1A6B46] text-white px-4 shrink-0">
                        <Sparkles className="h-4 w-4 mr-1.5" /> Draft email
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* Outbound prospects (CSV) */}
        <section className="mt-8 bg-white border border-[#E5E5E5] rounded-2xl" data-testid="prospects-section">
          <div className="px-6 py-5 border-b border-[#E5E5E5] flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="h-8 w-8 rounded-lg bg-[#114A31] flex items-center justify-center">
                <FileSpreadsheet className="h-4 w-4 text-white" />
              </span>
              <div>
                <h2 className="font-heading text-xl font-semibold text-[#1C1C1C]">Outbound prospects</h2>
                <p className="text-xs text-[#595D5A]">{prospectSummary.total} imported · {prospectSummary.contacted} contacted</p>
              </div>
            </div>
            <Button onClick={uploadCsv} disabled={uploading} data-testid="upload-csv-btn"
              className="rounded-full bg-[#114A31] hover:bg-[#1A6B46] text-white px-5">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-2" /> Import CSV</>}
            </Button>
          </div>
          <div className="p-4">
            {loadingProspects ? (
              <div className="py-10 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#114A31]" /></div>
            ) : prospects.length === 0 ? (
              <div className="py-12 text-center text-[#595D5A]" data-testid="prospects-empty">
                <FileSpreadsheet className="h-9 w-9 mx-auto mb-2" />
                <p className="text-sm">No prospects yet. Import a CSV to start outbound outreach.</p>
                <p className="mt-1 text-xs">Supported columns: <span className="font-mono">email</span> (required), <span className="font-mono">name</span>, <span className="font-mono">company</span>, <span className="font-mono">title</span>, <span className="font-mono">notes</span>.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {prospects.map((p) => (
                  <div key={p.id} data-testid={`prospect-${p.id}`}
                    className="rounded-xl border border-[#E5E5E5] p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Mail className="h-4 w-4 text-[#114A31] shrink-0" />
                        <span className="text-sm font-medium text-[#1C1C1C] truncate">{p.name || p.email}</span>
                        {p.status === "contacted" && (
                          <span className="text-[10px] uppercase tracking-wide bg-[#DCFCE7] text-[#166534] px-2 py-0.5 rounded-full">Contacted</span>
                        )}
                      </div>
                      <div className="mt-1 flex items-center gap-3 text-xs text-[#595D5A] flex-wrap">
                        <span className="truncate">{p.email}</span>
                        {p.company && <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{p.company}</span>}
                        {p.title && <span className="flex items-center gap-1"><Briefcase className="h-3 w-3" />{p.title}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button onClick={() => openDraft(p, "prospect")} data-testid={`prospect-draft-btn-${p.id}`}
                        className="rounded-full bg-[#114A31] hover:bg-[#1A6B46] text-white px-4">
                        <Sparkles className="h-4 w-4 mr-1.5" /> Draft email
                      </Button>
                      <button onClick={() => deleteProspect(p.id)} data-testid={`prospect-delete-${p.id}`}
                        className="h-9 w-9 rounded-full text-[#595D5A] hover:text-[#B91C1C] hover:bg-[#FEE2E2] flex items-center justify-center">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Sequence generator */}
        <section className="mt-8 bg-white border border-[#E5E5E5] rounded-2xl p-6 lg:p-8" data-testid="sequence-section">
          <div className="flex items-center gap-2">
            <span className="h-8 w-8 rounded-lg bg-[#114A31] flex items-center justify-center">
              <Layers className="h-4 w-4 text-white" />
            </span>
            <h2 className="font-heading text-xl font-semibold text-[#1C1C1C]">Outreach sequence generator</h2>
          </div>
          <p className="mt-2 text-sm text-[#595D5A]">Generate a 3-touch email campaign for a target audience.</p>
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <Input value={seqAudience} onChange={(e) => setSeqAudience(e.target.value)} data-testid="sequence-audience"
              className="rounded-xl border-[#E5E5E5]" placeholder="Describe your target audience" />
            <Button onClick={genSequence} disabled={seqLoading || !seqAudience.trim()} data-testid="generate-sequence-btn"
              className="rounded-full bg-[#114A31] hover:bg-[#1A6B46] text-white px-6 shrink-0">
              {seqLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Wand2 className="h-4 w-4 mr-2" /> Generate</>}
            </Button>
          </div>
          {sequence && (
            <div className="mt-6 space-y-4" data-testid="sequence-result">
              {sequence.map((em, i) => (
                <div key={i} className="rounded-xl border border-[#E5E5E5] bg-[#F9F9F8] p-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold bg-[#114A31] text-white rounded-full px-2.5 py-0.5">Step {em.step}</span>
                    <span className="text-xs text-[#595D5A]">{em.send_after}</span>
                  </div>
                  <p className="mt-2 font-semibold text-[#1C1C1C] text-sm">{em.subject}</p>
                  <p className="mt-1.5 text-sm text-[#595D5A] whitespace-pre-wrap">{em.body}</p>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Draft composer modal */}
      {draftFor && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" data-testid="draft-modal"
          onClick={() => !sending && setDraftFor(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-[#E5E5E5] flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[#114A31]" />
              <span className="font-heading font-semibold text-[#1C1C1C]">Personalized outreach</span>
            </div>
            <div className="p-6">
              {drafting ? (
                <div className="py-12 flex flex-col items-center text-[#595D5A]">
                  <Loader2 className="h-7 w-7 animate-spin text-[#114A31]" />
                  <p className="mt-3 text-sm">Drafting a personalized email…</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <Label className="text-[#1C1C1C]">To</Label>
                    <Input value={draft.to_email} onChange={(e) => setDraft({ ...draft, to_email: e.target.value })}
                      data-testid="draft-to" className="mt-1.5 rounded-xl border-[#E5E5E5]" />
                  </div>
                  <div>
                    <Label className="text-[#1C1C1C]">Subject</Label>
                    <Input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                      data-testid="draft-subject" className="mt-1.5 rounded-xl border-[#E5E5E5]" />
                  </div>
                  <div>
                    <Label className="text-[#1C1C1C]">Body</Label>
                    <Textarea value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                      data-testid="draft-body" className="mt-1.5 rounded-xl border-[#E5E5E5] min-h-[200px]" />
                  </div>
                  <div className="flex items-center justify-end gap-3 pt-2">
                    <Button variant="outline" onClick={() => setDraftFor(null)} disabled={sending}
                      data-testid="draft-cancel" className="rounded-full border-[#E5E5E5] text-[#1C1C1C]">Cancel</Button>
                    <Button onClick={sendDraft} disabled={sending} data-testid="draft-send"
                      className="rounded-full bg-[#114A31] hover:bg-[#1A6B46] text-white px-6">
                      {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" /> Send email</>}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
